import amplifyconfig from './amplifyconfiguration.json';

/**
 * Submit registration data to the EBS prospects API.
 *
 * Maps the legacy form-shape fields the rest of this app sends — firstname,
 * lastname, disclaimerChecked1, etc. — onto the EBS /prospects payload:
 *   - Top-level columns for the universal fields EBS persists discretely
 *     (firstName, lastName, email, country, phone, language, onsite, updatePos, referral).
 *   - customFields jsonb bag for variable per-event signup data
 *     (disclaimerChecked1 today; arbitrary keys later).
 *   - metadata for system markers (event slug for cross-system tracing).
 *
 * Backend is content-agnostic on customFields shape; the frontend owns
 * required-consent enforcement (react-hook-form `required: true`).
 *
 * @param {Object} data - Registration form data (legacy shape)
 * @returns {Promise<Object>} { ok, data: { id, createdAt, updatedAt } } or { ok: false, error }
 */
export async function submitRegistration(data) {
  try {
    const apiUrl = process.env.REACT_APP_BOOKING_API_URL;
    const apiKey = process.env.REACT_APP_BOOKING_API_KEY;
    const eventId = process.env.REACT_APP_EVENT_ID;

    if (!apiUrl || !apiKey || !eventId) {
      throw new Error(
        'EBS prospects config missing: REACT_APP_BOOKING_API_URL, REACT_APP_BOOKING_API_KEY, REACT_APP_EVENT_ID required.'
      );
    }

    // Pull legacy keys out so we can reshape into the EBS payload and stuff
    // anything else into customFields (forward-compatible with future opt-ins).
    const {
      firstname,
      lastname,
      email,
      country,
      phone,
      language,
      onsite,
      updatePOS,
      referral,
      event: eventSlug,
      disclaimerChecked1,
      disclaimerChecked2,
      ...rest
    } = data || {};

    const customFields = {
      ...(disclaimerChecked1 !== undefined ? { disclaimerChecked1 } : {}),
      ...(disclaimerChecked2 !== undefined ? { disclaimerChecked2 } : {}),
      ...rest,
    };

    const payload = {
      eventId,
      email,
      firstName: firstname,
      lastName: lastname,
      ...(country !== undefined ? { country } : {}),
      ...(phone !== undefined ? { phone } : {}),
      ...(language !== undefined ? { language } : {}),
      ...(onsite !== undefined ? { onsite } : {}),
      ...(updatePOS !== undefined ? { updatePos: updatePOS } : {}),
      ...(referral !== undefined ? { referral } : {}),
      ...(Object.keys(customFields).length > 0 ? { customFields } : {}),
      ...(eventSlug ? { metadata: { eventSlug } } : {}),
    };

    const response = await fetch(`${apiUrl.replace(/\/$/, '')}/prospects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    return {
      ok: true,
      data: responseData,
    };
  } catch (error) {
    console.error('Error submitting registration:', error);
    return {
      ok: false,
      error: error.message || 'Failed to submit registration',
    };
  }
}

/**
 * Search for a user by email address (for check-in)
 *
 * @param {string} email - Email address to search
 * @returns {Promise<Object>} Search result with user data or error
 */
export async function searchUserByEmail(email) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(
      `${apiUrl}/checkin/search?email=${encodeURIComponent(email)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to search for user',
      };
    }

    return data;
  } catch (error) {
    console.error('Error searching for user:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}

/**
 * Autocomplete search for users by email or lastname
 *
 * @param {string} query - Search query (min 2 characters)
 * @returns {Promise<Object>} Search result with suggestions array
 */
export async function searchUsersAutocomplete(query) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(
      `${apiUrl}/checkin/search?q=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to search',
        suggestions: [],
      };
    }

    return data;
  } catch (error) {
    console.error('Error in autocomplete search:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
      suggestions: [],
    };
  }
}

/**
 * Record a check-in for a user
 *
 * @param {string} email - Email address of user to check in
 * @returns {Promise<Object>} Check-in result
 */
export async function recordCheckin(email) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(`${apiUrl}/checkin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to check in user',
      };
    }

    return data;
  } catch (error) {
    console.error('Error checking in user:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}

/**
 * Get check-in statistics (today and last 7 days)
 *
 * @returns {Promise<Object>} Stats with checkinsToday and checkinsLast7Days
 */
export async function getCheckinStats() {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(`${apiUrl}/checkin/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to get check-in stats',
      };
    }

    return data;
  } catch (error) {
    console.error('Error getting check-in stats:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}

/**
 * Get recent check-ins
 *
 * @param {number} limit - Number of recent check-ins (10, 25, or 50)
 * @returns {Promise<Object>} Array of recent check-ins
 */
export async function getRecentCheckins(limit = 10) {
  try {
    const apiUrl = amplifyconfig.custom?.API?.RegistrationAPI?.endpoint
      || amplifyconfig.API?.REST?.RegistrationAPI?.endpoint
      || process.env.REACT_APP_API_URL
      || window.AMPLIFY_API_URL;

    if (!apiUrl) {
      throw new Error('API URL not configured');
    }

    const response = await fetch(`${apiUrl}/checkin/recent?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'UNKNOWN_ERROR',
        message: data.message || 'Failed to get recent check-ins',
        checkins: [],
      };
    }

    return data;
  } catch (error) {
    console.error('Error getting recent check-ins:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
      checkins: [],
    };
  }
}

/**
 * Export registrations via Members API as CSV
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Result with CSV data or error
 */
export async function exportRegistrations(startDate, endDate) {
  try {
    // Members API uses API Gateway with API key authentication
    // URL format: https://{api-id}.execute-api.{region}.amazonaws.com/{stage}/members/registration
    const membersApiUrl = process.env.REACT_APP_MEMBERS_API_URL;
    const apiKey = process.env.REACT_APP_MEMBERS_API_KEY;

    if (!membersApiUrl) {
      throw new Error('Members API URL not configured. Set REACT_APP_MEMBERS_API_URL in your environment.');
    }

    if (!apiKey) {
      throw new Error('Members API key not configured. Set REACT_APP_MEMBERS_API_KEY in your environment.');
    }

    const response = await fetch(
      `${membersApiUrl}/members/registration?startDate=${startDate}&endDate=${endDate}&event=All&type=csv`,
      {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || 'Failed to export registrations';
      } catch {
        errorMessage = errorText || 'Failed to export registrations';
      }
      return {
        success: false,
        error: 'EXPORT_FAILED',
        message: errorMessage,
      };
    }

    const csvData = await response.text();

    // Trigger download
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `registrations-${startDate}-to-${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      message: 'Export completed successfully',
    };
  } catch (error) {
    console.error('Error exporting registrations:', error);
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error occurred',
    };
  }
}
